import React from 'react'
import {
    Chip,
    makeStyles,
    Theme,
    Typography,
    useTheme,
} from '@material-ui/core'
import {
    ChatAgentName,
    ChatMessageContent,
    ConversationType,
    FunctionCall,
} from '../../types'
import { Container } from '../../../../../components/Container'
import { RenderCards } from './RenderCards'
import { TrainingVideoController } from '../../../../../hooks/useTrainingVideo'
import { ProjectPlanController } from '../../../../../hooks/useProjectPlan'
import { formatMessageTimestamp } from '../../../../../helpers/date'

interface ChatMessageProps {
    message: ChatMessageContent
    trainingVideoController: TrainingVideoController
    projectPlanController: ProjectPlanController
    conversationType?: ConversationType
}

// Keep in sync with the switch in `RenderCards`. Anything in this set is a
// function_call that *must* render its card regardless of how the backend
// tagged `agent_name` / `role` on the payload.
const CARD_FUNCTION_CALLS: ReadonlySet<FunctionCall> = new Set<FunctionCall>([
    'create_project',
    'create_deadline',
    'create_inspection',
    'how_to_video_list',
    'project_plan',
    'navigate_url',
    'contact_support',
])

export const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    trainingVideoController,
    projectPlanController,
    conversationType,
}) => {
    const theme = useTheme()
    const {
        role,
        function_call,
        message_items,
        agent_name,
        created_date,
    } = message
    const isUser = role === 'user'
    const timestampLabel = created_date
        ? formatMessageTimestamp(created_date)
        : ''

    const resolvedAgentName: ChatAgentName =
        agent_name ?? (role === 'system' ? 'system_event' : 'lori')
    const isHumanAdmin = resolvedAgentName === 'human_admin'

    // Cards win over agent_name. The backend currently tags every role='system'
    // payload (including card function_calls like create_project) with
    // agent_name='system_event', so if we early-returned on system_event we'd
    // render an empty Chip instead of the card. Route anything with a known
    // card function_call through RenderCards regardless of agent_name —
    // human_admin messages are still plain text and are excluded explicitly.
    const showCard =
        !!function_call &&
        CARD_FUNCTION_CALLS.has(function_call) &&
        conversationType !== 'VENDOR_APP' &&
        !isHumanAdmin

    // Only treat as a banner when we're *not* rendering a card. This protects
    // real banners (takeover/release, etc.) without swallowing card payloads
    // that happen to share the same agent_name.
    const isSystemEvent = resolvedAgentName === 'system_event' && !showCard

    if (isSystemEvent) {
        const bannerLabel = message_items?.[0]?.value
        // Banners without a visible label (e.g. connection_status frames) would
        // render as empty outlined pills — drop them entirely instead.
        if (!bannerLabel) return null
        return (
            <Container
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginBottom: theme.spacing(1),
                    padding: 0,
                }}
            >
                <Chip size="small" variant="outlined" label={bannerLabel} />
                {timestampLabel && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginTop: 4 }}
                    >
                        {timestampLabel}
                    </Typography>
                )}
            </Container>
        )
    }

    // Warm amber to match the "Taken Over" chip used in AdminChatView /
    // LiveTab, so support-agent styling is consistent end-to-end and visually
    // distinct from the user's primary-color bubble.
    const assistantBubbleBackground = isHumanAdmin
        ? '#fff3e0'
        : theme.palette.grey[100]
    const assistantBubbleColor = isHumanAdmin
        ? '#e65100'
        : theme.palette.text.primary

    return (
        <Container
            style={{
                display: 'flex',
                marginBottom: theme.spacing(1),
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                alignItems: 'center',
                position: 'relative',
                padding: 0,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isUser ? 'flex-end' : 'flex-start',
                    position: 'relative',
                    maxWidth: function_call ? '100%' : '90%',
                    width: function_call ? 'calc(100% - 24px)' : 'auto',
                    flex: function_call ? 1 : 'initial',
                }}
            >
                {isHumanAdmin && (
                    <Typography
                        variant="caption"
                        style={{
                            color: theme.palette.text.secondary,
                            marginBottom: 2,
                            marginLeft: theme.spacing(1),
                        }}
                    >
                        Support Agent
                    </Typography>
                )}
                {showCard ? (
                    <RenderCards
                        function_call={message}
                        trainingVideoController={trainingVideoController}
                        projectPlanController={projectPlanController}
                    />
                ) : (
                    <Typography
                        component="div"
                        style={{
                            padding: theme.spacing(1, 1),
                            borderRadius: theme.shape.borderRadius * 3,
                            backgroundColor: isUser
                                ? theme.palette.primary.main
                                : assistantBubbleBackground,
                            color: isUser
                                ? theme.palette.primary.contrastText
                                : assistantBubbleColor,
                            borderBottomRightRadius: isUser
                                ? theme.shape.borderRadius
                                : undefined,
                            borderBottomLeftRadius: !isUser
                                ? theme.shape.borderRadius
                                : undefined,
                            borderTopLeftRadius: isUser
                                ? theme.shape.borderRadius * 2
                                : undefined,
                            borderTopRightRadius: !isUser
                                ? theme.shape.borderRadius * 2
                                : undefined,
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {message_items?.[0]?.value}
                    </Typography>
                )}
                {timestampLabel && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{
                            marginTop: 4,
                            marginLeft: isUser ? 0 : theme.spacing(1),
                            marginRight: isUser ? theme.spacing(1) : 0,
                        }}
                    >
                        {timestampLabel}
                    </Typography>
                )}
            </div>
        </Container>
    )
}
